import { PropsWithChildren } from 'lib-shared-types-utils';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Starter = {
  /** Shown as the choice, and sent to the Sidekick as the prompt without onSelect. */
  label: string;
  /** Runs instead of sending the label; a returned string is sent to the Sidekick. */
  onSelect?: () => void | string;
};

type SidekickStarters = {
  starters: readonly Starter[];
  register: (starters: readonly Starter[]) => () => void;
};

const SidekickStartersContext = createContext<SidekickStarters | null>(null);

export const SidekickStartersProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [registered, setRegistered] = useState<readonly (readonly Starter[])[]>(
    [],
  );

  const register = useCallback((starters: readonly Starter[]) => {
    setRegistered((prev) => [...prev, starters]);
    return () =>
      setRegistered((prev) => prev.filter((entry) => entry !== starters));
  }, []);

  const value = useMemo<SidekickStarters>(
    () => ({ starters: registered.at(-1) ?? [], register }),
    [registered, register],
  );

  return (
    <SidekickStartersContext.Provider value={value}>
      {children}
    </SidekickStartersContext.Provider>
  );
};

export const useSidekickStarters = (): readonly Starter[] =>
  useContext(SidekickStartersContext)?.starters ?? [];

export const useRegisterStarters = (starters: readonly Starter[]): void => {
  const context = useContext(SidekickStartersContext);
  const register = context?.register;

  useEffect(() => register?.(starters), [register, starters]);
};
